import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Site, User } from 'src/app/classes/models';
import { AuthService } from 'src/app/services/auth.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SiteService } from 'src/app/services/site.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-site',
  templateUrl: './site.component.html',
  styleUrls: ['./site.component.less'],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SiteComponent {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private route: ActivatedRoute,
    private SiteService: SiteService,
    private ToastService: ToastService,
    private SharedDataService: SharedDataService,
    private Router: Router,
    private AuthService: AuthService
  ) {
    const paramMap = this.route.snapshot.paramMap;
    const SiteId = Number(paramMap.get('SiteId'));

    this.LoggedInUser = this.AuthService.getUser();

    if (!SiteId) {
      this.ToastService.addErrorToast('Error', 'No Site ID found.', {
        returnError: true
      });
    }

    this.getSite(SiteId).subscribe({
      next: (Site: Site) => {
        if (!Site) {
          this.ToastService.addErrorToast(
            'Error',
            'Error retrieving Site Record.',
            { returnError: true }
          );
        }

        this.Site = Site;
        this.SiteId = this.Site.id;
        this._cdr.markForCheck();
      }
    });

    this.SharedDataService.listenForEvent<Site | null | undefined>().subscribe({
      next: ({ eventName, data }) => {
        if (eventName === 'getSiteData') {
          this.getSite(SiteId).subscribe({
            next: (site: Site) => {
              if (!site) {
                this.ToastService.addErrorToast(
                  'Error',
                  'Error retrieving Site Record.',
                  { returnError: true }
                );
              }

              this.Site = site;
              this.SiteId = this.Site.id;

              if (this.Site) {
                this.SharedDataService.sendEvent(
                  'setSiteData',
                  new Site(this.Site)
                );
              }
              this._cdr.markForCheck();
            }
          });
        }

        if (eventName === 'updateSite' && data instanceof Site) {
          this.SiteService.saveSite(data).subscribe({
            next: (Site: Site) => {
              this.getSite(Site.id!);
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully saved Site.'
              );
              this.Router.navigate([
                'client',
                this.AuthService.getUserCompanyId(),
                'site',
                Site.id,
                'siteDetails'
              ]);
              this._cdr.markForCheck();
            }
          });
        }

        if (eventName === 'editSiteDetails') {
          this.Router.navigate(['editSite'], { relativeTo: this.route });
        }

        if (eventName === 'getSiteId') {
          this.SharedDataService.sendEvent('setSiteId', this.SiteId);
        }

        if (eventName === 'getLoggedInUser') {
          this.SharedDataService.sendEvent(
            'setLoggedInUser',
            new User(this.LoggedInUser)
          );
        }

        if (eventName === 'getHasEditPermissions') {
          this.SharedDataService.sendEvent(
            'setHasEditPermissions',
            this.hasEditPermissions()
          );
        }
      }
    });
  }

  SiteId?: number;
  Site?: Site;
  LoggedInUser: User;

  hasEditPermissions() {
    if (this.LoggedInUser) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      const acceptedRoles = new Set([
        'ADMIN_ADMIN',
        'ADMIN_FRAUD_TEAM',
        'CLIENT_ADMIN'
      ]);

      return !!role && acceptedRoles.has(role);
    }

    return false;
  }

  getSite(SiteId: number) {
    return this.SiteService.getSite({
      id: SiteId,
      Contact: JSON.stringify({
        isPrimary: true
      }),
      StreetAddress: JSON.stringify({})
    });
  }
}
